import React from 'react';
import PropTypes from 'prop-types';

import DebuggableComponent from '../utils/debuggable-component';

import '../css/seat-map.css';

import DragSelect from 'dragselect';

import Color from '../utils/color';
import Seat from '../models/seat';

import { isMobile } from 'react-device-detect';

import { ReactComponent as SeatAvailableSVG } from '../images/seating/siege-available.svg';
import { ReactComponent as IconPlus } from '../images/icon-plus.svg';
import { ReactComponent as IconMinus } from '../images/icon-minus.svg';

const Icon = ({ name, src }) => {
    if (React.isValidElement(src)) {
        return src;
    }

    return <img alt={`Icon ${name}`} src={src} />;
};

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    src: PropTypes.node,
};

const SeatStyle = ({ style, seat }) => {
    const { opacity, color, border, custom = '' } = style;
    return (
        <style>
            {`
            td[data-seat-id="${seat.uuid}"] path {
                ${opacity ? `opacity: ${opacity};` : ''}
                ${color ? `fill: ${color};` : ''}
                ${border ? `stroke: ${border};` : ''}
                ${custom}
            }
            `}
        </style>
    );
};

SeatStyle.propTypes = {
    style: PropTypes.shape({
        opacity: PropTypes.string,
        color: PropTypes.string,
        border: PropTypes.string,
        custom: PropTypes.string,
    }).isRequired,
    seat: PropTypes.shape({
        uuid: PropTypes.string,
    }).isRequired,
};

function CustomSeat({ label = '', children, style, seat, isMinimap = false }) {
    return (
        <>
            {style && !isMinimap && <SeatStyle style={style} seat={seat} />}
            {!isMinimap && <span className="seatmap-custom-label">{label}</span>}
            {children}
        </>
    );
}
CustomSeat.propTypes = {
    children: PropTypes.node,
    label: PropTypes.node,
    style: PropTypes.object,
    seat: PropTypes.object,
    isMinimap: PropTypes.bool,
};

export default class SeatMap extends DebuggableComponent {
    constructor(...args) {
        super(...args);

        let seats = this.props.zone.seats.map((s) => new Seat(s));

        const { defaultZoomLevel } = this.props;

        this.state = {
            seats,
            categories: null,
            zoomLevel: defaultZoomLevel ? defaultZoomLevel : isMobile ? 0.4 : 1,
        };

        this.dragselect = null;
        this.matrix = this.buildMatrix(seats);
    }

    shouldComponentUpdate(previousProps, previousState) {
    // Update component if one of the props changed
        for (let k in this.props) {
            if (!(k in previousProps) || previousProps[k] !== this.props[k]) {
                this.debug(
                    `Updating because prop ${k} changed from`,
                    previousProps[k],
                    'to',
                    this.props[k],
                );
                return true;
            }
        }

        if (previousState.zoomLevel !== this.state.zoomLevel) {
            return true;
        }

        if (previousProps.zone !== this.props.zone) {
            return true;
        }

        return false;
    }

    componentDidMount() {
        const { selectOnlySeats, draggable } = this.props;
        if (draggable && !this.dragselect && !isMobile) {
            this.dragselect = new DragSelect({
                selectables: document.querySelectorAll(
                    `div.${this.props.name} .seatmap-seat${selectOnlySeats ? '.selectable-seat' : ''}`,
                ),
                area: document.querySelector(
                    `div.${this.props.name} .seatmap-container`,
                ),
                callback: (selection) => {
                    if (selection.length <= 1) {
                        return;
                    }

                    let selectedSeats = selection
                        .map((td) => {
                            return this.state.seats.find(
                                (seat) => seat.pk === td.dataset.seatId,
                            );
                        })
                        .filter((seat) => seat);

                    if (!selectedSeats.length) {
                        return this.dragselect.reset();
                    }

                    if (selectedSeats.length === 1) {
                        return this.props.onSeatClicked(selectedSeats);
                    }

                    this.props.onSeatsSelected(selectedSeats);
                },
            });
        }
    }

    buildMatrix(seats) {
        let {
            zone: { name: zoneName },
        } = this.props;
        const timeTag = `Zone ${zoneName} matrix`;

        this.debug(`Creating seat matrix for zone ${zoneName}...`);
        this.time(timeTag);

        const offset = this.props.offset ? 1 : 0;
        let max_x = seats.reduce((acc, seat) => Math.max(seat.x, acc), 0);
        let max_y = seats.reduce((acc, seat) => Math.max(seat.y, acc), 0);

        if (this.props.offset) {
            max_y++;
            max_x++;
        }
        // new Array() === Array()
        // Map does not take into account empty
        // elements, so we fill them with null
        let columns = new Array(max_x + 1).fill(null);
        const matrix = new Array(max_y + 1).fill(null).map(() => [...columns]);

        // We reverse the coordinates
        // so we can loop on rows instead
        // of columns, simplifying everything later on
        seats.forEach((seat) => {
            matrix[seat.y + offset][seat.x + offset] = seat;
        });

        this.timeEnd(timeTag);
        this.debug(`Created seat matrix for zone ${zoneName}`, matrix);

        // reverses in place -.-
        matrix.reverse();
        return matrix;
    }

    zoom(out = false, delta = null) {
        delta = delta || 0.2 * (out ? 1 : -1);
        this.setState({
            zoomLevel: this.state.zoomLevel + delta,
        });
    }

    renderCategoriesStyles() {
        if (!this.props.categories) {
            return null;
        }

        const { showCategories } = this.props;

        let styles = this.props.categories.map((category) => {
            let darker = Color.fromString(category.color).darken(40).toString();
            return [
                `td.seatmap-seat.category-${category.pk} svg path.shp1 {
                    fill: ${category.color};
                }`,
                `td.seatmap-seat.category-${category.pk} svg path.shp0 {
                    fill: ${darker};
                }`,
            ];
        });

        let divs = null;

        // this saves ourselves 1ms :)
        if (showCategories) {
            divs = this.props.categories.map((category) => (
                <div
                    key={category.pk}
                    style={{
                        display: 'inline-block',
                        margin: '10px',
                        padding: '10px',
                        backgroundColor: category.color,
                        borderRadius: '5px',
                    }}
                >
                    {' '}
                    {category.name}{' '}
                </div>
            ));
        }

        return (
            <React.Fragment>
                <style>{styles}</style>
                {showCategories && (
                    <div>
            Categories
                        {divs}
                    </div>
                )}
            </React.Fragment>
        );
    }

    renderButtons() {
        if (!this.props.zoomControls) {
            return null;
        }

        return (
            <div className="seatmap-controls">
                <div
                    className="seatmap-controls-zoombutton"
                    onClick={() => this.zoom(true)}
                >
                    <IconPlus />
                </div>
                <div
                    className="seatmap-controls-zoombutton"
                    onClick={() => this.zoom(false)}
                >
                    <IconMinus />
                </div>
            </div>
        );
    }

    getSeatStyle() {
        const { zoomLevel } = this.state;
        const seatSize = parseInt(30 * zoomLevel);
        const style = {
            width: `${seatSize}px`, // added in JS to handle zoom ;)
            height: `${seatSize}px`,
        };

        return style;
    }

    renderSeat(seat, index, style) {
        let { SeatElement, seatElements, zone } = this.props;
        let seatDirection = zone.scene_location
            ? zone.scene_location.toLowerCase()
            : 'up';
        if (!seat) {
            return (
                <td
                    className={`seatmap-seat no-seat`}
                    key={`no-seat-${index}`}
                    style={style}
                />
            );
        }

        if (seat.state) {
            // If we have a state for the seat and
            // and a corresponding seatEelement, apply that one
            SeatElement = seatElements[seat.state] || SeatElement;
        }

        const { style: seatStyle = null } = seat;

        const onClickSeat = () => {
            // Handle mobile zoom
            if (this.state.zoomLevel < 0.6 && !this.props.isMinimap) {
                return this.zoom(false, 0.6);
            }

            return this.props.onSeatClicked(seat);
        };

        return (
            <td
                key={`seat${seat.x}x${seat.y}`}
                className={`seatmap-seat category-${seat.category} selectable-seat seatmap-seat-${seatDirection} ${seat.state}`}
                data-seat-id={seat.pk}
                data-category-id={seat.category}
                data-contingent-id={seat.contingent}
                onMouseOver={(e) => this.props.onSeatOver(seat, {element:e.target})}
                onMouseLeave={() => this.props.onSeatOver(null)}
                onClick={onClickSeat}
                style={style}
            >
                <CustomSeat
                    label={seat.custom_label}
                    style={seatStyle}
                    seat={seat}
                    isMinimap={this.props.isMinimap}
                >
                    {React.isValidElement(SeatElement) ? SeatElement : <SeatElement />}
                </CustomSeat>
            </td>
        );
    }

    renderRow(row, index, style) {
        let seats = row.map((seat, index) => this.renderSeat(seat, index, style));

        // Here we will assume that all seats have the same row number
        // If not, we must begin thinking of multiple seated zones!
        if (this.props.showRowNumbers) {
            let firstSeat = row.find((seat) => Boolean(seat));
            let rowName = firstSeat ? firstSeat.row : '-';
            seats.unshift(
                <td
                    key={`row-${rowName}`}
                    className={`seatmap-seat-${this.props.direction}`}
                    style={style}
                >
                    {rowName}
                </td>,
            );
        }

        return (
            <tr className={'seatmap-row'} key={`row-${index}`}>
                {seats}
            </tr>
        );
    }

    renderRows() {
        let style = this.getSeatStyle();
        return this.matrix.map((row, index) => this.renderRow(row, index, style));
    }

    renderContent() {
        return (
            <React.Fragment>
                {this.renderCategoriesStyles()}
                <div
                    className={`seatmap-react ${this.props.name}`}
                    id={`seatmap-${this.props.name}`}
                >
                    <table className="seatmap-container">
                        <tbody>{this.renderRows()}</tbody>
                    </table>
                    {this.renderButtons()}
                </div>
            </React.Fragment>
        );
    }
}

SeatMap.optionsTypes = {
    draggable: PropTypes.bool,
    selectOnlySeats: PropTypes.bool,
    offset: PropTypes.bool,
    showCategories: PropTypes.bool,
    zoomControls: PropTypes.bool,
    defaultZoomLevel: PropTypes.number,
    seatElements: PropTypes.object,
    showRowNumbers: PropTypes.bool,
};

SeatMap.defaultProps = {
    isMinimap: false,
    draggable: true,
    onSeatOver: () => {},
    categories: null,
    selectOnlySeats: true,
    offset: false,
    debug: false,
    onSeatClicked: () => {},
    onSeatsSelected: () => {},
    showCategories: false,
    SeatElement: SeatAvailableSVG,
    zoomControls: true,
    defaultZoomLevel: 1,
    seatElements: {},
    showRowNumbers: false,
};

SeatMap.propTypes = {
    isMinimap: PropTypes.bool,
    draggable: PropTypes.bool,
    zone: PropTypes.object.isRequired,
    onSeatClicked: PropTypes.func,
    onSeatsSelected: PropTypes.func,
    onSeatOver: PropTypes.func,
    categories: PropTypes.array,
    selectOnlySeats: PropTypes.bool,
    offset: PropTypes.bool,
    showCategories: PropTypes.bool,
    debug: PropTypes.bool,
    SeatElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    zoomControls: PropTypes.bool,
    defaultZoomLevel: PropTypes.number,
    name: PropTypes.string.isRequired,
    seatElements: PropTypes.object,
    showRowNumbers: PropTypes.bool,
};
